import apiClient from '../api/apiClient';

/**
 * Unified Progress Tracking Service
 * Handles form progress tracking across all registration steps
 */

export const REGISTRATION_STEPS = {
  NOT_STARTED: 'not_started',
  EMAIL_VERIFIED: 'email_verified',
  PASSWORD_SETUP: 'password_setup',
  INVESTMENT_TYPE_SET: 'investment_type_set',
  ORGANIZATION_SETUP_COMPLETED: 'organization_setup_completed',
  SUBSCRIPTION_IN_PROGRESS: 'subscription_in_progress',
  SUBSCRIPTION_SUBMITTED: 'subscription_submitted',
  COMPLETED: 'completed'
};

export const ROUTE_MAPPING = {
  [REGISTRATION_STEPS.NOT_STARTED]: '/',
  [REGISTRATION_STEPS.EMAIL_VERIFIED]: '/',
  [REGISTRATION_STEPS.PASSWORD_SETUP]: '/investment-type',
  [REGISTRATION_STEPS.INVESTMENT_TYPE_SET]: '/organization-setup',
  [REGISTRATION_STEPS.ORGANIZATION_SETUP_COMPLETED]: '/subscription',
  [REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS]: '/subscription',
  [REGISTRATION_STEPS.SUBSCRIPTION_SUBMITTED]: '/subscription',
  [REGISTRATION_STEPS.COMPLETED]: '/subscription'
};

class ProgressService {
  /**
   * Get current user progress from API
   */
  async getCurrentProgress(email) {
    try {
      console.log('🔄 ProgressService: Getting current progress for email:', email);
      
      const response = await apiClient.post('/investor/get-user-progress', { email });
      
      if (response.data.status === 'success') {
        const apiData = response.data.data;
        console.log('📡 ProgressService: API response data:', apiData);
        
        // Determine the ACTUAL registration step based on data present
        const actualStep = this.determineActualRegistrationStep(apiData);
        
        console.log('🎯 ProgressService: Final determined step:', actualStep);
        
        // Return data with corrected registration step
        return {
          ...apiData,
          registration_step: actualStep
        };
      }
      
      throw new Error(response.data.message || 'Failed to get progress');
    } catch (error) {
      console.error('❌ ProgressService: Error getting user progress:', error);
      
      // Fallback to localStorage if API fails
      console.log('🔄 ProgressService: Falling back to localStorage');
      return this.getProgressFromLocalStorage();
    }
  }

  /**
   * Determine actual registration step based on data present
   */
  determineActualRegistrationStep(userData) {
    console.log('🔍 ProgressService: Determining actual registration step for user data:', userData);
    
    const formData = userData.form_data || {};
    const hasSubscription = userData.has_subscription;
    const subscriptionStatus = userData.subscription_status;
    const investmentType = formData.investment_type;
    const storedStep = userData.registration_step;
    
    console.log('📊 ProgressService: Analysis data:', {
      formData: Object.keys(formData),
      hasSubscription,
      subscriptionStatus,
      investmentType,
      storedStep
    });
    
    // Check if subscription is submitted
    if (subscriptionStatus === 'submitted') {
      console.log('✅ ProgressService: Subscription submitted, returning SUBSCRIPTION_SUBMITTED');
      return REGISTRATION_STEPS.SUBSCRIPTION_SUBMITTED;
    }
    
    // Check if user has significant subscription progress
    if (hasSubscription && (
      formData.limitedPartnerName || 
      formData.socialSecurityNumber || 
      formData.residenceAddress ||
      formData.bankName ||
      (formData.individualIdCardDocuments && formData.individualIdCardDocuments.length > 0)
    )) {
      console.log('✅ ProgressService: Has subscription with significant progress, returning SUBSCRIPTION_IN_PROGRESS');
      return REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS;
    }
    
    // Check if user has started subscription (has current_step)
    if (hasSubscription && formData.current_step !== undefined) {
      console.log('✅ ProgressService: Has subscription with current step:', formData.current_step, 'returning SUBSCRIPTION_IN_PROGRESS');
      return REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS;
    }
    
    // Check if organization setup is completed
    // For individual investors, we consider org setup complete if they have investment_type
    if (investmentType) {
      if (investmentType === 'individual') {
        // Individual investors can proceed to subscription without explicit org setup
        console.log('✅ ProgressService: Individual investor with investment type, returning ORGANIZATION_SETUP_COMPLETED');
        return REGISTRATION_STEPS.ORGANIZATION_SETUP_COMPLETED;
      } else {
        // Non-individual investors need organization_name and entity_type
        if (formData.organization_name && formData.entity_type) {
          console.log('✅ ProgressService: Non-individual investor with org details, returning ORGANIZATION_SETUP_COMPLETED');
          return REGISTRATION_STEPS.ORGANIZATION_SETUP_COMPLETED;
        } else {
          console.log('✅ ProgressService: Non-individual investor without org details, returning INVESTMENT_TYPE_SET');
          return REGISTRATION_STEPS.INVESTMENT_TYPE_SET;
        }
      }
    }
    
    // Check if user has password setup complete
    if (formData.password_setup_complete && userData.email) {
      console.log('✅ ProgressService: Password setup complete, returning PASSWORD_SETUP');
      return REGISTRATION_STEPS.PASSWORD_SETUP;
    }
    
    // Default to the stored registration step or not started
    const finalStep = storedStep || REGISTRATION_STEPS.NOT_STARTED;
    console.log('✅ ProgressService: Using stored step or default:', finalStep);
    return finalStep;
  }

  /**
   * Update user progress in API and localStorage
   */
  async updateProgress(email, step, data = {}) {
    try {
      const response = await apiClient.post('/investor/update-progress', {
        email,
        registration_step: step,
        progress_data: data
      });

      if (response.data.status === 'success') {
        // Also update localStorage for offline access
        this.updateProgressInLocalStorage(step, data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Failed to update progress');
    } catch (error) {
      console.error('Error updating progress:', error);
      
      // Fallback to localStorage if API fails
      this.updateProgressInLocalStorage(step, data);
      throw error;
    }
  }

  /**
   * Get progress from localStorage (fallback)
   */
  getProgressFromLocalStorage() {
    const approvedEmail = localStorage.getItem('approved-email');
    const investmentType = localStorage.getItem('investment-type');
    const organizationName = localStorage.getItem('organization-name');
    const entityType = localStorage.getItem('entity-type');
    const token = localStorage.getItem('registration-token');
    const passwordSetupComplete = localStorage.getItem('password-setup-complete');

    let step = REGISTRATION_STEPS.NOT_STARTED;
    let formData = {};

    if (approvedEmail && token) {
      if (passwordSetupComplete) {
        step = REGISTRATION_STEPS.PASSWORD_SETUP;
        formData.password_setup_complete = true;
      } else {
        step = REGISTRATION_STEPS.EMAIL_VERIFIED;
      }
      
      if (investmentType) {
        step = REGISTRATION_STEPS.INVESTMENT_TYPE_SET;
        formData.investment_type = investmentType;
        
        // For individual investors, consider org setup complete
        if (investmentType === 'individual') {
          step = REGISTRATION_STEPS.ORGANIZATION_SETUP_COMPLETED;
        } else if (organizationName && entityType) {
          // Non-individual investors need organization details
          step = REGISTRATION_STEPS.ORGANIZATION_SETUP_COMPLETED;
          formData.organization_name = organizationName;
          formData.entity_type = entityType;
          formData.phone_number = localStorage.getItem('phone-number');
          formData.on_behalf_of_someone = localStorage.getItem('on-behalf-of-someone');
          formData.signing_individual_name = localStorage.getItem('signing-individual-name');
          formData.signing_individual_email = localStorage.getItem('signing-individual-email');
        }
        
        // Check if subscription is in progress
        const subscriptionStarted = localStorage.getItem('subscription-started');
        const currentStep = localStorage.getItem('current-step');
        if (subscriptionStarted || currentStep) {
          step = REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS;
          if (currentStep) {
            formData.current_step = parseInt(currentStep);
          }
        }
      }
    }

    return {
      registration_step: step,
      form_data: formData,
      email: approvedEmail,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  /**
   * Update progress in localStorage
   */
  updateProgressInLocalStorage(step, data) {
    localStorage.setItem('registration-step', step);
    localStorage.setItem('progress-updated', new Date().toISOString());
    
    // Store form data based on step
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        localStorage.setItem(key, data[key]);
        
        // Special handling for password setup complete
        if (key === 'password_setup_complete' && data[key] === true) {
          localStorage.setItem('password-setup-complete', 'true');
        }
        
        // Special handling for current step
        if (key === 'current_step' && data[key] !== undefined) {
          localStorage.setItem('current-step', data[key].toString());
        }
      }
    });
  }

  /**
   * Get the next route based on current step
   */
  getNextRoute(currentStep) {
    const steps = Object.values(REGISTRATION_STEPS);
    const currentIndex = steps.indexOf(currentStep);
    
    if (currentIndex >= 0 && currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      return ROUTE_MAPPING[nextStep] || '/investment-type';
    }
    
    return ROUTE_MAPPING[currentStep] || '/investment-type';
  }

  /**
   * Get the appropriate route for a given step
   */
  getRouteForStep(step) {
    return ROUTE_MAPPING[step] || '/investment-type';
  }

  /**
   * Get the exact current position for routing
   */
  getExactCurrentPosition(email) {
    return new Promise(async (resolve) => {
      try {
        const progress = await this.getCurrentProgress(email);
        
        // If user is in subscription form, return subscription route
        if (progress.registration_step === REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS) {
          resolve('/subscription');
          return;
        }
        
        // Otherwise use the standard route mapping
        const route = this.getRouteForStep(progress.registration_step);
        resolve(route);
      } catch (error) {
        console.error('Error getting exact position:', error);
        // Fallback to standard routing
        resolve('/investment-type');
      }
    });
  }

  /**
   * Check if user can access a specific route based on their progress
   */
  canAccessRoute(currentStep, requestedRoute) {
    const steps = Object.values(REGISTRATION_STEPS);
    const currentIndex = steps.indexOf(currentStep);
    
    // Find the step that corresponds to the requested route
    const requestedStep = Object.keys(ROUTE_MAPPING).find(
      step => ROUTE_MAPPING[step] === requestedRoute
    );
    
    if (!requestedStep) {
      return false;
    }
    
    const requestedIndex = steps.indexOf(requestedStep);
    
    // Users can access current step or previous steps
    return currentIndex >= requestedIndex;
  }

  /**
   * Save investment type data
   */
  async saveInvestmentType(email, investmentType) {
    const data = { investment_type: investmentType };
    
    try {
      console.log('🔄 ProgressService: Starting saveInvestmentType for:', email, investmentType);
      
      // Save to API (this already updates the progress in the backend)
      console.log('📡 ProgressService: Calling set-investment-type API...');
      const apiResponse = await apiClient.post('/investor/set-investment-type', {
        email,
        investment_type: investmentType
      });
      console.log('✅ ProgressService: API call successful:', apiResponse.data);
      
      // Update localStorage for offline access
      console.log('💾 ProgressService: Updating localStorage...');
      this.updateProgressInLocalStorage(REGISTRATION_STEPS.INVESTMENT_TYPE_SET, data);
      console.log('✅ ProgressService: localStorage updated successfully');
      
      return { success: true };
    } catch (error) {
      console.error('❌ ProgressService: Error saving investment type:', error);
      
      // Still update localStorage even if API fails
      this.updateProgressInLocalStorage(REGISTRATION_STEPS.INVESTMENT_TYPE_SET, data);
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Save organization setup data
   */
  async saveOrganizationSetup(email, organizationData) {
    const data = {
      organization_name: organizationData.organizationName,
      entity_type: organizationData.entityType,
      phone_number: organizationData.phoneNumber,
      on_behalf_of_someone: organizationData.onBehalfOfSomeone,
      signing_individual_name: organizationData.signingIndividualName,
      signing_individual_email: organizationData.signingIndividualEmail
    };
    
    try {
      // Save to API
      await apiClient.post('/investor/setup-organization', {
        email,
        ...data
      });
      
      // Update progress
      await this.updateProgress(email, REGISTRATION_STEPS.ORGANIZATION_SETUP_COMPLETED, data);
      
      return { success: true };
    } catch (error) {
      console.error('Error saving organization setup:', error);
      
      // Still update localStorage even if API fails
      this.updateProgressInLocalStorage(REGISTRATION_STEPS.ORGANIZATION_SETUP_COMPLETED, data);
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Save subscription progress
   */
  async saveSubscriptionProgress(email, subscriptionData, currentStep = null) {
    const data = {
      subscription_data: subscriptionData,
      subscription_started: 'true',
      current_step: currentStep
    };
    
    try {
      // Save to API
      await apiClient.post('/investor/save-subscription', {
        email,
        subscription_data: subscriptionData,
        current_step: currentStep
      });
      
      // Update progress
      await this.updateProgress(email, REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS, data);
      
      return { success: true };
    } catch (error) {
      console.error('Error saving subscription progress:', error);
      
      // Still update localStorage even if API fails
      this.updateProgressInLocalStorage(REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS, data);
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Save current step position in subscription form
   */
  async saveCurrentStep(email, stepIndex, sectionName = null) {
    const data = {
      current_step: stepIndex,
      current_section: sectionName,
      last_updated: new Date().toISOString()
    };
    
    try {
      console.log('💾 ProgressService: Saving current step:', stepIndex, 'section:', sectionName);
      
      // Save to API
      await apiClient.post('/investor/save-subscription', {
        email,
        current_step: stepIndex,
        current_section: sectionName
      });
      
      // Update progress
      await this.updateProgress(email, REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS, data);
      
      return { success: true };
    } catch (error) {
      console.error('Error saving current step:', error);
      
      // Still update localStorage even if API fails
      this.updateProgressInLocalStorage(REGISTRATION_STEPS.SUBSCRIPTION_IN_PROGRESS, data);
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Submit final subscription
   */
  async submitSubscription(email, subscriptionData) {
    const data = {
      subscription_data: subscriptionData,
      subscription_submitted: 'true'
    };
    
    try {
      // Submit to API
      await apiClient.post('/investor/submit-subscription', {
        email,
        subscription_data: subscriptionData
      });
      
      // Update progress
      await this.updateProgress(email, REGISTRATION_STEPS.SUBSCRIPTION_SUBMITTED, data);
      
      return { success: true };
    } catch (error) {
      console.error('Error submitting subscription:', error);
      
      // Still update localStorage even if API fails
      this.updateProgressInLocalStorage(REGISTRATION_STEPS.SUBSCRIPTION_SUBMITTED, data);
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Load saved form data for a specific step
   */
  async loadFormData(email, step) {
    try {
      const progress = await this.getCurrentProgress(email);
      
      if (progress && progress.form_data) {
        return progress.form_data;
      }
      
      return {};
    } catch (error) {
      console.error('Error loading form data:', error);
      return {};
    }
  }

  /**
   * Clear all progress data (for testing/reset)
   */
  async clearProgress(email) {
    try {
      await apiClient.post('/investor/clear-progress', { email });
      
      // Clear localStorage
      const keysToRemove = [
        'registration-step',
        'progress-updated',
        'investment-type',
        'organization-name',
        'entity-type',
        'phone-number',
        'on-behalf-of-someone',
        'signing-individual-name',
        'signing-individual-email',
        'subscription-started',
        'subscription-submitted'
      ];
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      return { success: true };
    } catch (error) {
      console.error('Error clearing progress:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new ProgressService(); 